import React, { useState, useEffect, useMemo, useRef } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useCoreContext } from "../../../context/global/CoreContext";
import { useTMLogContext } from "../../../context/domain/operations/TMLogContext";
import { getLatestInspector } from "../../../features/operations/tmlog/helpers";
import {
  createTMReport,
  updateTMReport,
  getNextTMId,
  getTMAttachmentFolderPath,
  getTMReportPdfUrl,
  sendToInspector,
  toggleTMReportLocked,
} from "../../../services/api/tmlog";
import {
  Button,
  SignaturePad,
  ConfirmModal,
  Checklist,
  PdfViewerModal,
} from "../../../components/ui";
import {
  JobInformation,
  ReportInformation,
  WorkDetail,
  Comments,
  TMTable,
  AddLineItemButton,
  SendToInspectorModal,
  InspectorSentConfirmationModal,
} from "../../../features/operations/tmlog";
import {
  useTicketAttachments,
  TicketAttachmentsSection,
} from "../../../features/tickets";
import { handleFiles } from "../../../services/api/core";
import { WeeklyContext } from "../../../context/global/WeeklyContext";
import { getScopesByWeek } from "../../../services/api/dispatch";
import {
  getWeekStart,
  getDateInLocalTZFromDateStr,
  getTodayInPT,
} from "../../../utils/datetime";
import { getUserFullName } from "../../../utils/business";
import { useFormHybrid } from "../../../utils/common/useFormHybrid";
/**
 * T&M Report Form Page
 * Create/edit T&M report tags
 *
 * IMPORTANT: Backend PDF Generation
 * -----------------------------------
 * When the "Generate PDF" checkbox is enabled (to be implemented) and the save
 * button is clicked, this component will send generate_pdf=true to the backend API.
 *
 * The backend then automatically generates a PDF in the API endpoint:
 * - Location: backend/webapp/tmlog/api.py (TMReportViewSet)
 * - Method: create() or update() depending on operation
 * - PDF Generator: backend/webapp/tmlog/pdf_gen.py (generate_tm_pdf function)
 * - Format: City_DFA.pdf template (based on report_type field)
 * - Storage: Saved locally when DEBUG=True, uploaded to Egnyte when DEBUG=False
 *
 * PDF generation is CONDITIONAL - only happens when checkbox is ticked.
 * Does not generate automatically on every save.
 *
 * TODO: Add "Generate PDF" checkbox above save buttons and pass flag to API
 */
const TMReport = () => {
  const navigate = useNavigate();
  const { allJobsList } = useCoreContext();
  const { getReportById, refreshReports, reportNewItem } = useTMLogContext();
  const { id } = useParams();

  useEffect(() => {
    document.title = "T&M Report - R&S";
  }, []);

  // Hybrid form state management
  // State: fields that affect UI rendering
  // Refs: fields only needed for API submission
  const {
    state: formData,
    setState: setFormData,
    refs,
    handleSubmit,
    isSubmitting,
  } = useFormHybrid({
    initialState: {
      job: null, // Used by JobInformation, ReportInformation, WorkDetail
      date_performed: getTodayInPT(), // Used by JobInformation, WorkDetail
      description: "", // Used by WorkDetail textarea
      comments: "", // Used by Comments textarea
      line_items: [], // Used by TMTable
      pm_signature: null, // Used by SignaturePad
    },
    initialRefs: {
      tm_id: null, // Display-only, computed from nextTMId
      report_type: "City_DFA", // Default matches ReportInformation component
      pco_number: "", // Simple text input, no UI logic
      contractor_name: "R&S Construction Management Inc.", // Default matches ReportInformation component
      title: "", // Internal create-time title used for Egnyte folder naming
      generate_pdf: true, // PDF generation checkbox - default to true to match defaultChecked={true}
    },
    onSubmit: async (payload) => {
      // Merge prefilled line items if not already set
      if (!payload.line_items?.length && prefilledLineItems?.length) {
        payload.line_items = prefilledLineItems;
      }

      let reportId = id;
      if (id) {
        await updateTMReport(id, payload);
      } else {
        const shouldGeneratePdfAfterAttachments =
          pendingFiles.length > 0 && refs.current.generate_pdf;
        const createPayload = shouldGeneratePdfAfterAttachments
          ? { ...payload, generate_pdf: false }
          : payload;
        const createdReport = await createTMReport(createPayload);
        reportId = createdReport.id;
        // Upload pending attachments for new report, then update PDF with attachment list
        if (pendingFiles.length > 0 && createdReport.tm_id) {
          try {
            const filesForUpload = pendingFiles.map((file) => ({
              file,
              filename: file.name,
              type: file.type?.startsWith("image/") ? "image" : "pdf",
            }));
            await handleFiles({
              files: filesForUpload,
              action: "upload_tm_attachment",
              additional_data: { tm_id: createdReport.tm_id },
            });
            const names = pendingFiles.map((f) => f.name);
            setPendingFiles([]);
            if (shouldGeneratePdfAfterAttachments) {
              await updateTMReport(reportId, {
                generate_pdf: true,
                attachment_filenames: names,
              });
            }
          } catch (uploadErr) {
            console.error("Failed to upload T&M attachments:", uploadErr);
          }
        }
      }
      if (id) {
        await refreshReports({ silent: true });
      } else {
        await reportNewItem(reportId);
      }

      // If we have pending inspector email data, send to inspector instead of navigating
      if (pendingInspectorEmail.current) {
        const emailData = pendingInspectorEmail.current;
        pendingInspectorEmail.current = null; // Clear the ref

        try {
          await sendToInspector(reportId, emailData);
          await refreshReports({ silent: true });

          // Refresh report data to get updated locked status
          const updatedReport = await getReportById(reportId);
          setIsLocked(updatedReport.locked || false);

          // Update URL to reflect new report ID (for new reports)
          if (!id) {
            window.history.replaceState(
              null,
              "",
              `/app/operations/tm-log/report/${reportId}`
            );
          }

          // Show success confirmation
          setSentInspectorEmail(emailData.inspector_email);
          setSentCopyEmail(emailData.inspector_copy_email || "");
          setShowInspectorSentModal(true);
        } catch (error) {
          console.error("Error sending to inspector:", error);
          alert(
            "Report saved, but failed to send to inspector. Please try again."
          );
        } finally {
          setIsSendingToInspector(false);
        }
      } else {
        navigate("/operations/tm-log");
      }
    },
  });

  const [scopes, setScopes] = useState(null);
  const [isLoadingScopes, setIsLoadingScopes] = useState(false);
  const [nextTMId, setNextTMId] = useState(null);
  const [prefilledLineItems, setPrefilledLineItems] = useState(null); // Line items prefilled from scopes
  const [showReloadConfirm, setShowReloadConfirm] = useState(false); // Modal state for reload confirmation
  const [showPreview, setShowPreview] = useState(true); // State for collapsible preview table
  const [dataLoaded, setDataLoaded] = useState(false); // Track when ref data has been loaded
  const [edited, setEdited] = useState(false); // Using form line_items vs dispatch prefills (Reload button)
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false); // True after user edits since last save/load
  const initialData = useRef(null); // Used to revert/compare user changes
  const pendingInspectorEmail = useRef(null); // Stores email data when saving before sending to inspector
  const [showInspectorModal, setShowInspectorModal] = useState(false); // Modal state for send to inspector
  const [isLocked, setIsLocked] = useState(false); // Track if report is locked
  const [isSendingToInspector, setIsSendingToInspector] = useState(false); // Track send to inspector submission
  const [showInspectorSentModal, setShowInspectorSentModal] = useState(false); // Modal state for inspector sent confirmation
  const [sentInspectorEmail, setSentInspectorEmail] = useState(""); // Email address where report was sent
  const [sentCopyEmail, setSentCopyEmail] = useState(""); // CC email address where copy was sent
  const [showUnlockWarningModal, setShowUnlockWarningModal] = useState(false); // Modal state for unlock warning
  const [showPdfModal, setShowPdfModal] = useState(false); // View PDF modal (edit mode only)

  // Attachments for T&M report (only when editing existing report)
  const {
    attachments,
    pendingFiles,
    setPendingFiles,
    isLoadingAttachments,
    isUploadingFiles,
    handleDragEnd,
    getOrderedAttachmentFilenames,
    handleUploadAttachments,
  } = useTicketAttachments({
    ticketId: id ?? null,
    ticketIdentifier: nextTMId ?? undefined,
    getAttachmentFolderPathApi: getTMAttachmentFolderPath,
    uploadAction: "upload_tm_attachment",
    uploadAdditionalData:
      nextTMId != null ? { tm_id: nextTMId } : undefined,
    coverPageLabel: null, // T&M backend adds cover; we only send attachment filenames
  });

  /**
   * Process scopes data to extract concrete, equipment, subcontractor, and worker_assignment
   * into prefilled line items format.
   * Only includes items from scopes matching the selected job and date.
   *
   * @param {Array<Object>} scopesData Dispatch scopes for the week.
   * @param {number} jobId Selected job id.
   * @param {string} datePerformed Performed date key.
   * @returns {Array<Object>} Prefilled T&M line items.
   */
  const processScopesToLineItems = (scopesData, jobId, datePerformed) => {
    if (!scopesData || !Array.isArray(scopesData) || !jobId || !datePerformed) {
      return [];
    }

    const lineItems = [];
    let idCounter = Date.now();

    scopesData.forEach((scope) => {
      // Only process scopes that match the selected job and date
      if (scope.job?.id !== jobId || scope.date !== datePerformed) {
        return;
      }
      // Process concrete items
      if (scope.concrete && Array.isArray(scope.concrete)) {
        scope.concrete.forEach((concreteItem) => {
          lineItems.push({
            id: idCounter++,
            rate_rt: concreteItem.mix?.cost_per_yard || 0,
            type: "concrete",
            unit: concreteItem.unit || "Yards",
            quantity: concreteItem.yards || "",
            option_id: concreteItem.mix?.id || 0,
            option_name:
              concreteItem.mix?.design_number || "Name not set in Admin Panel",
          });
        });
      }

      // Process equipment items (asset.category === 'Equipment')
      if (scope.equipment && Array.isArray(scope.equipment)) {
        scope.equipment.forEach((equipmentItem) => {
          lineItems.push({
            id: idCounter++,
            internal_id: equipmentItem.internal_id || null,
            notes: equipmentItem.notes || "",
            rate_rt: equipmentItem.asset?.price_per_unit_rt || 0,
            rate_ot: equipmentItem.asset?.price_per_unit_ot || 0,
            rt_hours: 8,
            type: "equipment",
            unit: "Hours",
            quantity: 1,
            option_id: equipmentItem.asset?.id || null,
            option_name:
              equipmentItem.asset?.name || "Name not set in Admin Panel",
          });
        });
      }

      // Process subcontractor items
      if (scope.subcontractor && Array.isArray(scope.subcontractor)) {
        scope.subcontractor.forEach((subcontractorItem) => {
          lineItems.push({
            id: idCounter++,
            rate_rt: subcontractorItem.contractor.cost_per_hour || 0,
            type: "subcontractors",
            unit: "Count",
            quantity: subcontractorItem.hours || 1,
            option_id: subcontractorItem.contractor?.id || null,
            option_name:
              subcontractorItem.contractor?.name ||
              "Name not set in Admin Panel",
          });
        });
      }

      // Process dumping items
      if (scope.dumping && Array.isArray(scope.dumping)) {
        scope.dumping.forEach((dumpingItem) => {
          lineItems.push({
            id: idCounter++,
            rate_rt: dumpingItem.contractor.cost_per_unit || 0,
            type: "dumping",
            unit: "Count",
            quantity: dumpingItem.quantity || 1,
            option_id: dumpingItem.contractor?.id || null,
            option_name:
              dumpingItem.contractor?.name || "Name not set in Admin Panel",
          });
        });
      }

      // Process worker_assignment items (labor)
      if (scope.worker_assignment && Array.isArray(scope.worker_assignment)) {
        scope.worker_assignment.forEach((workerItem) => {
          // Construct worker name from first and last name fields
          const workerName =
            getUserFullName(workerItem) ||
            workerItem.name ||
            workerItem.option_name ||
            workerItem.worker_name ||
            "";

          lineItems.push({
            id: idCounter++,
            rate_rt: workerItem.worker_cost || 0,
            rate_ot: workerItem.worker_cost
              ? parseFloat(workerItem.worker_cost * 1.5).toFixed(2)
              : 0,
            type: "labor",
            unit: workerItem.unit || "Hours",
            rt_hours: 8,
            option_id:
              workerItem.id || workerItem.option_id || workerItem.worker_id,
            option_name: workerName,
          });
        });
      }
    });

    return lineItems;
  };

  // Load report data if editing
  useEffect(() => {
    if (id) {
      /**
       * Loads an existing T&M report into form state and refs.
       *
       * @returns {Promise<void>}
       */
      const loadReport = async () => {
        try {
          const data = await getReportById(id);
          initialData.current = data; // Store initial data

          // Split data into state and refs based on our hybrid model
          setFormData({
            job: data.job,
            date_performed: data.date_performed,
            description: data.description || "",
            comments: data.comments || "",
            line_items: data.line_items || [],
            pm_signature: data.pm_signature,
          });

          // Set ref values
          refs.current.tm_id = data.tm_id;
          refs.current.report_type = data.report_type || "";
          refs.current.pco_number = data.pco_number || "";
          refs.current.contractor_name = data.contractor_name || "";
          refs.current.title = data.title || "";
          refs.current.generate_pdf = true; // Default to true to match checkbox defaultChecked

          // Set locked status
          setIsLocked(data.locked || false);

          // Mark data as loaded to trigger component remount
          setDataLoaded(true);
          setHasUnsavedChanges(false);
          // edited=true means show saved report line_items (not fresh dispatch prefills)
          if (data.line_items && data.line_items.length > 0) {
            setEdited(true);
          }
        } catch (error) {
          console.error("Error loading report:", error);
        }
      };
      loadReport();
    } else {
      // For new reports, mark as loaded immediately
      setDataLoaded(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, getReportById, setFormData]); // refs intentionally excluded - it's a stable ref object

  // Fetch next TM ID when job changes
  useEffect(() => {
    if (formData?.job === initialData.current?.job) {
      // If job hasn't changed from initial data, use existing tm_id
      setNextTMId(initialData.current?.tm_id || null);
      refs.current.tm_id = initialData.current?.tm_id || null;
      return;
    }
    if (formData?.job) {
      /**
       * Fetches the next TM id for the selected job number.
       *
       * @returns {Promise<void>}
       */
      const fetchTMId = async () => {
        try {
          const selectedJob = allJobsList?.find(
            (job) => job.id === formData.job
          );
          if (selectedJob?.number) {
            const response = await getNextTMId(selectedJob.number);
            // Update refs with the generated TM ID (not state, as it's display-only)
            refs.current.tm_id = response.tm_id;
            setNextTMId(response.tm_id);
          }
        } catch (error) {
          console.error("Error fetching TM ID:", error);
          setNextTMId(null);
        }
      };
      fetchTMId();
    } else if (refs.current?.tm_id) {
      // If editing and tm_id exists, use it
      setNextTMId(refs.current.tm_id);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formData?.job, id, allJobsList]); // refs intentionally excluded - it's a stable ref object

  // Load scopes when date is selected
  useEffect(() => {
    if (formData?.date_performed) {
      /**
       * Loads dispatch scopes for the performed date and prefills line items.
       *
       * @returns {Promise<void>}
       */
      const loadScopes = async () => {
        try {
          setIsLoadingScopes(true);
          const dateObj = getDateInLocalTZFromDateStr(formData.date_performed);
          const weekStart = getWeekStart(dateObj);
          const scopesData = await getScopesByWeek(weekStart);
          setScopes(scopesData);

          // Process scopes data into prefilled line items
          const processedLineItems = processScopesToLineItems(
            scopesData,
            formData.job,
            formData.date_performed
          );
          setPrefilledLineItems(processedLineItems);
        } catch (error) {
          console.error("Error loading scopes:", error);
        } finally {
          setIsLoadingScopes(false);
        }
      };
      loadScopes();
    }
  }, [formData?.date_performed, formData?.job]);

  // Required fields for Save / Save & Send to Inspector: Job ID, Date Performed, Work Description
  /**
   * Lists required fields still empty (job, date performed, work description).
   *
   * @returns {Array<string>} Missing field labels.
   */
  const getMissingRequiredFields = () => {
    const missing = [];
    if (!formData?.job) missing.push("Job ID");
    const dateVal = formData?.date_performed;
    if (!dateVal || (typeof dateVal === "string" && !dateVal.trim())) missing.push("Date Performed");
    if (!formData?.description?.trim()) missing.push("Work Description");
    return missing;
  };

  const canSave = getMissingRequiredFields().length === 0;

  const saveChecklistConditions = [
    { met: !!formData?.job, message: "Please select a Job ID." },
    {
      met: !!(formData?.date_performed && (typeof formData.date_performed !== "string" || formData.date_performed.trim())),
      message: "Please enter Date Performed.",
    },
    { met: !!(formData?.description?.trim()), message: "Please enter Work Description." },
  ];

  /**
   * Validates required fields and saves the report (optionally generating a PDF).
   *
   * @param {React.FormEvent|undefined} e Submit event.
   * @returns {Promise<void>}
   */
  const handleSave = async (e) => {
    const missing = getMissingRequiredFields();
    if (missing.length > 0) {
      alert(`Please fill in all required fields: ${missing.join(", ")}`);
      return;
    }
    if (refs.current.generate_pdf) {
      refs.current.attachment_filenames = getOrderedAttachmentFilenames();
    }
    await handleSubmit(e);
    setHasUnsavedChanges(false);
  };

  /**
   * Returns to the T&M log without saving.
   *
   * @returns {void}
   */
  const handleCancel = () => {
    navigate("/operations/tm-log");
  };

  /**
   * Saves the report then emails it to the inspector (after required-field checks).
   *
   * @param {Object} emailData Inspector email payload.
   * @returns {Promise<void>}
   */
  const handleSendToInspector = async (emailData) => {
    const missing = getMissingRequiredFields();
    if (missing.length > 0) {
      alert(`Please fill in all required fields: ${missing.join(", ")}`);
      return;
    }
    // Close the modal first
    setShowInspectorModal(false);
    setIsSendingToInspector(true);

    // Store email data - onSubmit will check this and send to inspector after saving
    pendingInspectorEmail.current = emailData;

    if (refs.current.generate_pdf) {
      refs.current.attachment_filenames = getOrderedAttachmentFilenames();
    }
    // Trigger save - onSubmit will handle sending to inspector
    await handleSubmit();
    setHasUnsavedChanges(false);
  };

  /**
   * Locks the report, or opens the unlock warning when already locked.
   *
   * @returns {Promise<void>}
   */
  const handleToggleLock = async () => {
    if (!id) {
      console.error("Cannot toggle lock: Report ID not found");
      return;
    }

    // If currently locked, show warning modal before unlocking
    if (isLocked) {
      setShowUnlockWarningModal(true);
      return;
    }

    // If not locked, proceed with locking (no warning needed)
    try {
      await toggleTMReportLocked(id);
      await refreshReports({ silent: true });

      // Refresh report data to get updated locked status
      const updatedReport = await getReportById(id);
      setIsLocked(updatedReport.locked || false);
    } catch (error) {
      console.error("Error toggling lock:", error);
      alert("Failed to toggle lock status. Please try again.");
    }
  };

  /**
   * Unlocks the report after the warning modal is confirmed.
   *
   * @returns {Promise<void>}
   */
  const handleConfirmUnlock = async () => {
    if (!id) {
      console.error("Cannot unlock: Report ID not found");
      return;
    }

    try {
      await toggleTMReportLocked(id);
      await refreshReports({ silent: true });

      // Refresh report data to get updated locked status
      const updatedReport = await getReportById(id);
      setIsLocked(updatedReport.locked || false);
      setShowUnlockWarningModal(false);
    } catch (error) {
      console.error("Error unlocking report:", error);
      alert("Failed to unlock report. Please try again.");
      setShowUnlockWarningModal(false);
    }
  };

  /**
   * Replaces line items with dispatch prefills after confirm.
   *
   * @returns {void}
   */
  const handleReloadFromDispatch = () => {
    setFormData({
      ...formData,
      line_items: prefilledLineItems || [],
    });
    setShowReloadConfirm(false);
    setEdited(false);
    setHasUnsavedChanges(true);
  };

  /**
   * Opens the reload-from-dispatch confirm modal when line items exist.
   *
   * @returns {void}
   */
  const handleReloadButtonClick = () => {
    if (formData.line_items) {
      setShowReloadConfirm(true);
    }
  };

  // Handler to add new line items from AddLineItemButton
  /**
   * Appends a line item (or starts from dispatch prefills if the list is empty).
   *
   * @param {Object} newItem New line item.
   * @returns {void}
   */
  const handleAddItem = (newItem) => {
    setFormData({
      ...formData,
      line_items: [
        ...(formData.line_items.length > 0
          ? formData.line_items
          : prefilledLineItems || []),
        newItem,
      ],
    });
    setEdited(true);
    setHasUnsavedChanges(true);
  };

  /**
   * Apply form state updates and mark the report as having unsaved changes.
   *
   * @param {Object|Function} next - Partial state object or updater function.
   * @returns {void}
   */
  const updateFormData = (next) => {
    setFormData(next);
    setHasUnsavedChanges(true);
  };

  // Handler to add multiple line items at once from TMLineItem multi-select
  /**
   * Applies a multi-select: first item updates `existingItemID`, the rest are appended.
   *
   * @param {Array<Object>} newItems Selected line items.
   * @param {number|string} existingItemID Existing row id to replace, if any.
   * @returns {void}
   */
  const handleAddMultipleItems = (newItems, existingItemID) => {
    if (!newItems || newItems.length === 0) return;

    updateFormData((prevFormData) => {
      if (existingItemID && newItems.length > 0) {
        // Update existing item with first selection, add rest as new items
        const firstItem = newItems[0];
        const remainingItems = newItems.slice(1);

        const updatedLineItems = prevFormData.line_items.map((item) =>
          item.id === existingItemID
            ? {
              ...item,
              option_id: firstItem.option_id,
              option_name: firstItem.option_name,
            }
            : item
        );

        return {
          ...prevFormData,
          line_items: [...updatedLineItems, ...remainingItems],
        };
      }

      // No existing item, just add all new items
      return {
        ...prevFormData,
        line_items: [...prevFormData.line_items, ...newItems],
      };
    });
    setEdited(true);
  };

  // Create a minimal WeeklyContext value with only the scopes data needed
  const weeklyContextValue = useMemo(
    () => ({
      scopes,
      isLoading: isLoadingScopes,
    }),
    [scopes, isLoadingScopes]
  );

  return (
    <WeeklyContext.Provider value={weeklyContextValue}>
      <div className="p-2 sm:p-3 w-screen mx-auto">
        <div className="bg-green-900 text-white p-3 sm:p-4 rounded-t-lg">
          <div className="flex justify-between items-center">
            <div>
              <h1 className="text-xl sm:text-2xl font-bold">
                Time & Materials Tag
              </h1>
              <p className="text-sm">Construction Project Documentation</p>
            </div>
            {id ? (
              <div className="flex flex-col items-end gap-2">
                {isLocked ? (
                  <div className="bg-yellow-400 text-gray-900 px-3 py-1 rounded-md font-semibold text-sm">
                    🔒 Form Locked
                  </div>
                ) : null}
                <Button
                  variant="custom"
                  text="View PDF"
                  onClick={() => setShowPdfModal(true)}
                  className="px-3 py-1.5 text-sm font-semibold bg-white text-green-900 hover:bg-green-50 border border-green-200"
                />
              </div>
            ) : null}
          </div>
        </div>

        <div className="bg-white rounded-b-lg shadow p-2 sm:p-6">
          <div className="space-y-4 sm:space-y-6">
            <div className="border-b pb-4">
              <JobInformation
                data={formData}
                onChange={updateFormData}
                readOnly={isLocked}
              />
            </div>

            <div className="border-b pb-4">
              <ReportInformation
                key={`${id || "new"}-${dataLoaded}`} // Remount when data loads or report changes
                data={refs.current}
                onChange={(updates) => {
                  Object.assign(refs.current, updates);
                  setHasUnsavedChanges(true);
                }}
                readOnly={isLocked}
                titleReadOnly={!!id}
                nextTMId={nextTMId}
              />
            </div>

            <div className="border-b pb-4">
              <WorkDetail
                data={formData}
                onChange={updateFormData}
                readOnly={isLocked}
              />
            </div>

            <div className="border-b pb-4">
              <div className="flex justify-between items-center mb-4">
                <h2 className="hidden md:block text-lg font-semibold text-gray-700">
                  Time & Materials
                </h2>
                <h2 className="block md:hidden text-lg font-semibold text-gray-700">
                  T&M
                </h2>
                <div
                  className={`flex gap-2 ${isLocked ? "opacity-50 pointer-events-none" : ""}`}
                >
                  <Button
                    variant="custom"
                    text={
                      !prefilledLineItems || prefilledLineItems.length === 0
                        ? "No T&M in Dispatch"
                        : edited
                          ? "Reload from Dispatch"
                          : "Loaded from Dispatch"
                    }
                    onClick={handleReloadButtonClick}
                    className={`h-[24px] text-sm font-normal ${!prefilledLineItems || prefilledLineItems.length === 0 ? " pointer-events-none text-gray-400 " : edited ? " hover:text-green-800 hover:scale-[1.05]" : "pointer-events-none text-gray-400 "} px-2`}
                  />
                  <AddLineItemButton
                    onAddItem={handleAddItem}
                    disabled={isLocked}
                  />
                </div>
              </div>
              <div className="overflow-x-auto">
                <TMTable
                  items={
                    edited ? formData.line_items : prefilledLineItems || []
                  }
                  onAddItems={handleAddMultipleItems}
                  onChange={(items) => {
                    updateFormData({ ...formData, line_items: items });
                    setEdited(true);
                  }}
                  readOnly={isLocked}
                />
              </div>
            </div>

            <Comments
              data={formData}
              onChange={updateFormData}
              readOnly={isLocked}
              title="Internal Comments"
              placeholder="Add internal comments or notes..."
            />

            {getLatestInspector(initialData.current?.inspectors) &&
              isLocked && (
                <div>
                  <h2 className="text-lg font-semibold text-gray-700 mb-4">
                    Inspector Comments (Read Only)
                  </h2>
                  <div className="border rounded-lg p-3 bg-gray-50 min-h-[80px]">
                    {getLatestInspector(initialData.current?.inspectors)
                      .inspector_comments || (
                        <p className="text-sm text-gray-500 italic">
                          No comments added.
                        </p>
                      )}
                  </div>
                  <h2 className="text-lg font-semibold text-gray-700 mb-4 mt-4">
                    Inspector Signature (Read Only)
                  </h2>
                  <SignaturePad
                    value={
                      getLatestInspector(initialData.current?.inspectors)
                        ?.inspector_signature
                    }
                    readOnly={true}
                    height={156}
                  />
                </div>
              )}

            <div className="border-b pb-4">
              <h2 className="text-lg font-semibold text-gray-700 mb-4">
                Project Manager Signature
              </h2>
              <SignaturePad
                value={formData?.pm_signature}
                initialValue={initialData.current?.pm_signature}
                onChange={(signature) =>
                  updateFormData({ ...formData, pm_signature: signature })
                }
                readOnly={isLocked}
                height={156}
              />
            </div>
          </div>

          {/* Attachments (create and edit) */}
          <TicketAttachmentsSection
            ticketIdentifier={nextTMId}
            coverSubtitle="T&M Report"
            attachments={attachments}
            handleDragEnd={handleDragEnd}
            pendingFiles={pendingFiles}
            pendingFilesHelpText={!id ? "Files will be uploaded when T&M Report is saved." : "Files will be uploaded when the upload button is clicked."}
            setPendingFiles={setPendingFiles}
            isLoadingAttachments={isLoadingAttachments}
            isUploadingFiles={isUploadingFiles}
            handleUploadAttachments={handleUploadAttachments}
            disabled={isLocked || isSubmitting || !formData.job}
            showUploadButton={!!id}
            showNotCreatedMessage={!formData.job}
          />

          {/* PDF Generation Checkbox */}
          {!isLocked && (
            <div className="mt-6 mb-4 flex items-center gap-2 p-3 bg-blue-50 border border-blue-200 rounded">
              <input
                type="checkbox"
                id="generate-pdf"
                name="generate_pdf"
                defaultChecked={true}
                data-ref="true"
                onChange={(e) => {
                  refs.current.generate_pdf = e.target.checked;
                }}
                className="w-4 h-4 text-blue-600 bg-gray-100 border-gray-300 rounded focus:ring-blue-500"
              />
              <label
                htmlFor="generate-pdf"
                className="text-sm font-medium text-gray-700"
              >
                Save to Egnyte
              </label>
            </div>
          )}

          {!isLocked && (
            <Checklist
              className="w-full mb-3"
              conditions={saveChecklistConditions}
            />
          )}

          <div className="flex flex-col sm:flex-row gap-2 sm:gap-4">
            {isLocked ? (
              <Button
                onClick={handleToggleLock}
                variant="warningcustom"
                text="Unlock Form"
                className="flex-1 bg-yellow-400 hover:bg-yellow-500 text-gray-900 p-2"
              />
            ) : (
              <>
                <Button
                  onClick={handleCancel}
                  variant="warningcustom"
                  text="Cancel"
                  className="flex-1 text-black bg-yellow-100 hover:bg-red-500 hover:text-white py-1.5 px-2"
                />
                <Button
                  onClick={handleSave}
                  disabled={isSubmitting || !canSave}
                  variant="secondary"
                  text={isSubmitting ? "Saving..." : "Save"}
                  className="flex-1"
                />
                <Button
                  onClick={() => {
                    const missing = getMissingRequiredFields();
                    if (missing.length > 0) {
                      alert(`Please fill in all required fields: ${missing.join(", ")}`);
                      return;
                    }
                    setShowInspectorModal(true);
                  }}
                  disabled={isSubmitting || !canSave}
                  variant="primary"
                  text={
                    isSendingToInspector
                      ? "Sending..."
                      : "Save & Send to Inspector"
                  }
                  className="flex-1"
                />
              </>
            )}
          </div>
        </div>

        <ConfirmModal
          isOpen={showReloadConfirm}
          title="Reload Line Items from Dispatch?"
          text="This will replace all current Time & Materials items with data from the dispatch. Any unsaved changes you've made will be lost. Are you sure you want to continue?"
          buttonText="Reload"
          onConfirm={() => {
            handleReloadFromDispatch();
            setFormData({ ...formData, line_items: [] });
          }}
          onClose={() => {
            setShowReloadConfirm(false);
            setShowPreview(true);
          }}
        >
          {/* Collapsible preview table */}
          <div className="border rounded-lg bg-gray-50 p-3 text-left">
            <button
              type="button"
              onClick={() => setShowPreview(!showPreview)}
              className="w-full flex justify-between items-center text-sm font-semibold text-gray-700 hover:text-gray-900"
            >
              <span>Preview Replacement Data</span>
              <span className="text-xs">
                {showPreview ? "▼ Hide" : "▶ Show"}
              </span>
            </button>

            {showPreview && (
              <div className="mt-3 max-h-96 overflow-auto">
                <TMTable items={prefilledLineItems || []} readOnly={true} />
              </div>
            )}
          </div>
        </ConfirmModal>

        {/* Send to Inspector Modal */}
        <SendToInspectorModal
          isOpen={showInspectorModal}
          onClose={() => setShowInspectorModal(false)}
          onConfirm={handleSendToInspector}
          isSubmitting={isSendingToInspector}
          report={initialData.current}
        />

        {/* Inspector Sent Confirmation Modal */}
        <InspectorSentConfirmationModal
          isOpen={showInspectorSentModal}
          onClose={() => {
            setShowInspectorSentModal(false);
            navigate("/operations/tm-log");
          }}
          inspectorEmail={sentInspectorEmail}
          copyEmail={sentCopyEmail}
        />

        {id ? (
          <PdfViewerModal
            isOpen={showPdfModal}
            onClose={() => setShowPdfModal(false)}
            pdfUrl={getTMReportPdfUrl(id)}
            authenticated
            title="T&M Report PDF"
            subtitle={
              hasUnsavedChanges
                ? "Save any changes before viewing — unsaved edits will not appear in this PDF."
                : undefined
            }
          />
        ) : null}

        {/* Unlock Warning Modal */}
        <ConfirmModal
          isOpen={showUnlockWarningModal}
          onClose={() => setShowUnlockWarningModal(false)}
          onConfirm={handleConfirmUnlock}
          title="⚠️ Unlock Report?"
          buttonText="Unlock Report"
          buttonClassName="bg-orange-600 hover:bg-orange-700"
        >
          <div className="space-y-4">
            <div className="bg-orange-50 border border-orange-300 rounded p-4">
              <p className="text-sm font-semibold text-orange-800 mb-2">
                Warning: This action cannot be reversed!
              </p>
              <p className="text-sm text-orange-700">
                This report was locked because an inspector signed it. If you
                unlock it:
              </p>
              <ul className="text-sm text-left text-orange-700 mt-2 ml-5 space-y-1 list-disc">
                <li>The report will become editable again</li>
                <li>Changes can be made to the signed report</li>
                <li>
                  The report will remain unlocked until the inspector signs it
                  again
                </li>
              </ul>
            </div>

            <p className="text-sm text-gray-700">
              Are you sure you want to unlock this report?
            </p>
          </div>
        </ConfirmModal>
      </div>
    </WeeklyContext.Provider>
  );
};

export default TMReport;
